import React, { useContext, useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import type { DateBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import styles from './TriggerStep.module.css';

interface DateTriggerConfigDialogProps {
  onClose: () => void;
  triggerMetadata: DateBasedTriggerMetadata;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  monthName: new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2024, i, 1)),
}));

function getDaysInMonth(month: number): number {
  // Use 2024 as a leap year to allow Feb 29
  return new Date(2024, month, 0).getDate();
}

export const DateTriggerConfigDialog: React.FC<DateTriggerConfigDialogProps> = ({
  onClose,
  triggerMetadata,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateTrigger } = editor;
  const defaultMonth = triggerMetadata.monthToTrigger || 1;
  const [selectedMonth, setSelectedMonth] = useState<number>(defaultMonth);
  const defaultDay = triggerMetadata.dayToTrigger || 1;
  const [selectedDay, setSelectedDay] = useState<number>(defaultDay);

  const availableDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [selectedMonth]);

  useEffect(() => {
    const maxDayForMonth = getDaysInMonth(selectedMonth);
    if (selectedDay > maxDayForMonth) {
      setSelectedDay(maxDayForMonth);
    }
  }, [selectedMonth, selectedDay]);

  const handleDone = () => {
    const newTriggerMetadata = structuredClone(triggerMetadata);
    newTriggerMetadata.monthToTrigger = selectedMonth;
    newTriggerMetadata.dayToTrigger = selectedDay;
    updateTrigger(newTriggerMetadata);
    onClose();
  };

  const formatSelectedDate = () => {
    const date = new Date(2024, selectedMonth - 1, selectedDay);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dateTriggerDialog} open>
        <header>
          <hgroup>
            <h2>Select Date</h2>
            <p>Choose the date and time for this campaign to start</p>
          </hgroup>
          <button onClick={onClose} type="button" aria-label="Close">
            <X />
          </button>
        </header>

        <main>
          <output>{formatSelectedDate()}, annually</output>
          <fieldset>
            <label>
              Month
              <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
                {MONTHS.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.monthName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Day
              <select value={selectedDay} onChange={e => setSelectedDay(Number(e.target.value))}>
                {availableDays.map(day => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleDone} />
      </dialog>
    </>
  );
};
